import { useState, useEffect, useMemo } from 'react'

// 工程順。辞書順にしない（doing が done の後ろに来ると進み方が読めない）。
// 並びは ops/taskboard.py の statuses と同じ意味で使う。
const STATUS = {
  doing:  { label: '進行中', tone: 'bg-sky-500/20 text-sky-300 border-sky-700/50' },
  review: { label: '評価/レビュー', tone: 'bg-amber-500/20 text-amber-300 border-amber-700/50' },
  inbox:  { label: '未着手', tone: 'bg-gray-600/30 text-gray-300 border-gray-600' },
  hold:   { label: '保留', tone: 'bg-purple-500/20 text-purple-300 border-purple-700/50' },
  done:   { label: '完了', tone: 'bg-green-500/20 text-green-400 border-green-700/50' },
}
const ORDER = ['doing', 'review', 'inbox', 'hold', 'done']

const age = (sec) => {
  if (typeof sec !== 'number') return ''
  if (sec < 60) return `${sec}秒前`
  if (sec < 3600) return `${Math.floor(sec / 60)}分前`
  if (sec < 86400) return `${Math.floor(sec / 3600)}時間前`
  return `${Math.floor(sec / 86400)}日前`
}

export default function OpsBoard() {
  const [state, setState] = useState({ loading: true })
  const [domain, setDomain] = useState('')      // '' = 全部
  const [showDone, setShowDone] = useState(false)

  useEffect(() => {
    let alive = true
    fetch('/cruz/dev/board.json', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json())
      .then((d) => alive && setState({ loading: false, ...d }))
      .catch(() => alive && setState({ loading: false, ok: false, message: '通信に失敗しました' }))
    return () => { alive = false }
  }, [])

  const board = state.board
  const tasks = useMemo(() => {
    const all = (board?.tasks || []).filter((t) => !t.archived)
    return all
      .filter((t) => (domain ? t.domain === domain : true))
      .filter((t) => (showDone ? true : t.status !== 'done'))
      // 工程順 → ID 順。ORDER に無い status は後ろへ（黙って混ぜない）
      .sort((a, b) => {
        const ia = ORDER.indexOf(a.status), ib = ORDER.indexOf(b.status)
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.id.localeCompare(b.id)
      })
  }, [board, domain, showDone])

  if (state.loading) return <p className="text-gray-500">読み込み中…</p>

  if (!state.ok) {
    return (
      <div className="bg-amber-950/40 border border-amber-800 rounded-xl p-4 text-amber-200">
        <p className="font-bold mb-1">台帳を表示できません</p>
        <p className="text-sm text-amber-300/80">{state.message}</p>
      </div>
    )
  }
  if (!board?.available) {
    return (
      <div className="bg-amber-950/40 border border-amber-800 rounded-xl p-4 text-amber-200">
        <p className="font-bold mb-1">台帳がありません</p>
        <p className="text-sm text-amber-300/80">{board?.reason}</p>
      </div>
    )
  }

  const counts = {}
  ;(board.tasks || []).filter((t) => !t.archived).forEach((t) => {
    counts[t.status] = (counts[t.status] || 0) + 1
  })

  return (
    <div>
      {/* 台帳の md が board.json より後に触られている＝集計が古い。黙って新しい顔をしない */}
      {board.stale && (
        <p className="mb-4 text-xs text-amber-300/90 bg-amber-950/30 border border-amber-900 rounded px-3 py-2">
          台帳の集計が古くなっています（{board.newest_source} の方が新しい）。
          作業マシンで <code>python ops/taskboard.py</code> を流すと直ります。
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap mb-4 text-sm">
        <button
          type="button"
          onClick={() => setDomain('')}
          className={`px-3 py-1 rounded border transition ${domain === ''
            ? 'bg-gray-700 text-white border-gray-600' : 'border-gray-800 text-gray-400 hover:bg-gray-800'}`}
        >
          全部 {(board.tasks || []).filter((t) => !t.archived).length}
        </button>
        {(board.domains || []).map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDomain(d === domain ? '' : d)}
            className={`px-3 py-1 rounded border transition ${d === domain
              ? 'bg-gray-700 text-white border-gray-600' : 'border-gray-800 text-gray-400 hover:bg-gray-800'}`}
          >
            {d}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-gray-400 cursor-pointer">
          <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
          完了も出す
        </label>
      </div>

      <div className="flex gap-2 flex-wrap mb-4 text-xs">
        {ORDER.filter((s) => counts[s]).map((s) => (
          <span key={s} className={`px-2 py-0.5 rounded border ${STATUS[s].tone}`}>
            {STATUS[s].label} {counts[s]}
          </span>
        ))}
      </div>

      {tasks.length === 0 && <p className="text-gray-500 text-sm">該当する案件がありません</p>}

      <ul className="space-y-2">
        {tasks.map((t) => {
          const s = STATUS[t.status] || { label: t.status, tone: 'bg-gray-700/40 text-gray-300 border-gray-600' }
          return (
            <li key={t.id} className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono font-bold text-sm">{t.id}</span>
                <span className={`px-2 py-0.5 rounded border text-xs ${s.tone}`}>{s.label}</span>
                <span className="text-xs text-gray-500">{t.domain}</span>
                <span className="text-xs text-gray-600 ml-auto">
                  {t.created}{t.closed ? ` → ${t.closed}` : ''}
                </span>
              </div>
              <p className="mt-1 text-sm">{t.title}</p>
              {t.links && (
                <p className="mt-1 text-xs text-gray-500 truncate" title={t.links}>🔗 {t.links}</p>
              )}
              {/* source は「なぜこの案件が在るか」。台帳の一次情報なので出す */}
              {t.source && (
                <p className="mt-1 text-xs text-gray-600 line-clamp-2" title={t.source}>{t.source}</p>
              )}
              <p className="mt-1 text-[11px] text-gray-700 font-mono">{t.file}</p>
            </li>
          )
        })}
      </ul>

      <p className="mt-4 text-xs text-gray-600">
        集計 {board.generated_at}（スナップショットは {age(state.snapshot_age_seconds)}）・
        正本は ops/&lt;domain&gt;/*.md、集計は ops/taskboard.py
      </p>
    </div>
  )
}
